import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, resolveImageUrl } from '@/lib/axios';
import { Product, ProductCategory } from '@/types';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Image as ImageIcon, Sparkles } from 'lucide-react';

export default function StorePage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCat, setSelectedCat] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [selectedCat]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const url = selectedCat ? `/products?categoryId=${selectedCat}` : '/products';
      const [prodRes, catRes] = await Promise.all([
        api.get<Product[]>(url).catch(() => ({ data: [] })),
        api.get<ProductCategory[]>('/product-categories').catch(() => ({ data: [] }))
      ]);
      setProducts(Array.isArray(prodRes?.data) ? prodRes.data : []);
      setCategories(Array.isArray(catRes?.data) ? catRes.data : []);
    } catch {
      setProducts([]);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-10 md:py-16 px-4 max-w-6xl">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Catálogo de Creaciones</h1>
          <p className="text-sm text-muted-foreground mt-1">Piezas 3D creadas en nuestro taller con estética Japandi y materiales nobles.</p>
        </div>

        {/* Categorías */}
        <div className="flex gap-2 overflow-x-auto pb-2 w-full md:w-auto scrollbar-hide">
          <Button
            variant={selectedCat === null ? 'default' : 'outline'}
            className="rounded-full text-xs md:text-sm"
            onClick={() => setSelectedCat(null)}
          >
            Todos
          </Button>
          {(Array.isArray(categories) ? categories : []).map(cat => (
            <Button
              key={cat.id}
              variant={selectedCat === Number(cat.id) ? 'default' : 'outline'}
              className="rounded-full text-xs md:text-sm"
              onClick={() => setSelectedCat(Number(cat.id))}
            >
              {cat.name}
            </Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-20 text-muted-foreground">Cargando creaciones...</div>
      ) : (!Array.isArray(products) || products.length === 0) ? (
        <div className="text-center py-20 border border-dashed rounded-2xl">
          <p className="text-muted-foreground">No hay productos visibles en esta categoría por el momento.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {(Array.isArray(products) ? products : []).map(p => {
            const price = p.isOnSale && p.promotionalPrice ? p.promotionalPrice : p.salePrice;
            return (
              <Link key={p.id} to={`/tienda/${p.slug || p.id}`}>
                <Card className="h-full hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden border border-border/70 bg-card group flex flex-col justify-between">
                  <div>
                    <div className="aspect-square bg-muted relative overflow-hidden flex items-center justify-center">
                      {p.mainImageUrl ? (
                        <img
                          src={resolveImageUrl(p.mainImageUrl)}
                          alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <ImageIcon className="h-12 w-12 text-muted-foreground/30" />
                      )}
                      {p.isOnSale && (
                        <Badge className="absolute top-2 left-2 bg-destructive text-destructive-foreground text-[10px] shadow-sm">
                          <Sparkles className="h-3 w-3 mr-1" /> OFERTA
                        </Badge>
                      )}
                      {p.stockQuantity > 0 ? (
                        <Badge className="absolute top-2 right-2 bg-emerald-600 text-white text-[10px] shadow-sm font-medium">
                          En stock ({p.stockQuantity})
                        </Badge>
                      ) : !p.allowBackorder ? (
                        <Badge className="absolute top-2 right-2 bg-zinc-800 text-zinc-100 text-[10px] shadow-sm">
                          Agotado
                        </Badge>
                      ) : null}
                    </div>
                    <CardContent className="p-3 md:p-4 space-y-1">
                      <h3 className="font-semibold text-sm md:text-base line-clamp-1 group-hover:text-primary transition-colors">
                        {p.name}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {p.shortDescription || 'Diseño 3D sustentable'}
                      </p>
                    </CardContent>
                  </div>

                  <div className="px-3 pb-3 md:px-4 md:pb-4 flex justify-between items-baseline pt-2 border-t border-border/40">
                    <div>
                      <span className="font-bold text-base md:text-lg text-primary">
                        ${price.toLocaleString('es-AR')}
                      </span>
                      {p.isOnSale && p.promotionalPrice && (
                        <span className="text-[11px] line-through text-muted-foreground ml-1.5">
                          ${p.salePrice.toLocaleString('es-AR')}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {p.stockQuantity > 0
                        ? "⚡ Envío 24/48hs"
                        : p.allowBackorder
                        ? `🔨 ${p.estimatedDeliveryDays || 5}d entrega`
                        : "Sin stock"}
                    </span>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
